/**
 * Android SDK config — data-layer client (feature #184).
 *
 * Thin typed wrapper over `apiClient` for the seven endpoints backing the
 * Android SDK operator page:
 *   - GET    /api/v1/android-sdk/config?app_id=&sdk_version=
 *   - GET    /api/v1/android-sdk/apps
 *   - POST   /api/v1/android-sdk/apps
 *   - PATCH  /api/v1/android-sdk/apps/:id
 *   - DELETE /api/v1/android-sdk/apps/:id
 *   - POST   /api/v1/android-sdk/installations
 *   - GET    /api/v1/android-sdk/installations?app_id=&limit=
 *
 * Mirrors the server-side view shape in apps/api/src/routes/android-sdk.ts.
 * Keep this file dependency-free beyond `apiClient` so pure helpers
 * (validation, formatting, summaries, diffs) stay trivially testable.
 */

import { apiClient } from "../../api-client";

// ---------------------------------------------------------------------------
// Types — mirrors the backend view shape.
// ---------------------------------------------------------------------------

export interface AndroidSdkConfig {
	enabled_events: string[];
	disabled_events: string[];
	rate_limit_per_min: number;
	feature_flags: Record<string, boolean>;
}

export interface AndroidAppView {
	id: string;
	package_name: string;
	app_name: string;
	sha256_fingerprints: string[];
	min_sdk_version: string;
	config: AndroidSdkConfig;
	active: boolean;
	created_at: number;
	updated_at: number;
}

export interface AndroidInstallationView {
	id: string;
	app_id: string;
	install_id: string;
	sdk_version: string;
	device_model: string;
	os_version: string;
	android_id_hash: string;
	created_at: number;
}

export interface AndroidSdkConfigResponse extends AndroidSdkConfig {
	min_sdk_version: string;
	upgrade_recommended: boolean;
}

export interface AndroidAppsResponse {
	apps: AndroidAppView[];
}

export interface AndroidInstallationsResponse {
	app_id: string | null;
	limit: number;
	installations: AndroidInstallationView[];
}

// ---------------------------------------------------------------------------
// Validation — mirrors server-side bounds.
// ---------------------------------------------------------------------------

export const ANDROID_PACKAGE_MAX_BYTES = 255;
const PACKAGE_SEGMENT_RE = /^[a-z][a-z0-9_]*$/;
const FINGERPRINT_PAIR_RE = /^[A-Fa-f0-9]{2}$/;
const SEMVER_RE =
	/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([0-9A-Za-z.-]+))?$/;
const EVENT_NAME_RE = /^[a-z][a-z0-9_]{0,63}$/;

export function byteLength(str: string): number {
	return new TextEncoder().encode(str).length;
}

export function isValidPackageName(v: unknown): v is string {
	if (typeof v !== "string") return false;
	if (v.length === 0 || v.length > ANDROID_PACKAGE_MAX_BYTES) return false;
	const segs = v.split(".");
	if (segs.length < 2) return false;
	for (const s of segs) if (!PACKAGE_SEGMENT_RE.test(s)) return false;
	return true;
}

export function isValidFingerprint(v: unknown): v is string {
	if (typeof v !== "string") return false;
	const parts = v.split(":");
	if (parts.length !== 32) return false;
	for (const p of parts) if (!FINGERPRINT_PAIR_RE.test(p)) return false;
	return true;
}

export function normalizeFingerprintInput(v: string): string | null {
	const trimmed = v.trim().toUpperCase();
	return isValidFingerprint(trimmed) ? trimmed : null;
}

export function isValidSemVer(v: unknown): v is string {
	return typeof v === "string" && SEMVER_RE.test(v);
}

export function isValidEventName(v: unknown): v is string {
	return typeof v === "string" && EVENT_NAME_RE.test(v);
}

export function isValidAppName(v: unknown): v is string {
	if (typeof v !== "string") return false;
	const trimmed = v.trim();
	return trimmed.length > 0 && byteLength(trimmed) <= 120;
}

// ---------------------------------------------------------------------------
// Remote bindings — called directly from the page / mutations.
// ---------------------------------------------------------------------------

const BASE = "/android-sdk";

export interface CreateAppArgs {
	package_name: string;
	app_name: string;
	sha256_fingerprints: string[];
	min_sdk_version: string;
	config?: Partial<AndroidSdkConfig>;
}

export async function fetchAndroidApps(): Promise<AndroidAppsResponse> {
	return apiClient.get<AndroidAppsResponse>(`${BASE}/apps`);
}

export async function createAndroidApp(
	args: CreateAppArgs,
): Promise<AndroidAppView> {
	return apiClient.post<AndroidAppView>(`${BASE}/apps`, args);
}

export interface UpdateAppArgs {
	app_name?: string;
	sha256_fingerprints?: string[];
	min_sdk_version?: string;
	config?: AndroidSdkConfig;
	active?: boolean;
}

export async function updateAndroidApp(
	id: string,
	args: UpdateAppArgs,
): Promise<AndroidAppView> {
	return apiClient.patch<AndroidAppView>(
		`${BASE}/apps/${encodeURIComponent(id)}`,
		args,
	);
}

export async function deleteAndroidApp(
	id: string,
): Promise<{ ok: true; id: string }> {
	return apiClient.delete<{ ok: true; id: string }>(
		`${BASE}/apps/${encodeURIComponent(id)}`,
	);
}

export async function fetchAndroidConfig(
	appId: string,
	sdkVersion?: string,
): Promise<AndroidSdkConfigResponse> {
	return apiClient.get<AndroidSdkConfigResponse>(`${BASE}/config`, {
		query: {
			app_id: appId,
			...(sdkVersion ? { sdk_version: sdkVersion } : {}),
		},
	});
}

export async function fetchAndroidInstallations(
	appId: string | null,
	limit = 100,
): Promise<AndroidInstallationsResponse> {
	return apiClient.get<AndroidInstallationsResponse>(
		`${BASE}/installations`,
		{
			query: {
				...(appId ? { app_id: appId } : {}),
				limit,
			},
		},
	);
}

// ---------------------------------------------------------------------------
// Pure helpers — list summaries, formatters, diff preview.
// ---------------------------------------------------------------------------

export interface AndroidAppsSummary {
	total: number;
	active: number;
	disabled: number;
	total_fingerprints: number;
	latest_update_at: number | null;
}

export function computeAppsSummary(
	rows: readonly AndroidAppView[],
): AndroidAppsSummary {
	let active = 0;
	let disabled = 0;
	let totalFps = 0;
	let latest: number | null = null;
	for (const r of rows) {
		if (r.active) active += 1;
		else disabled += 1;
		totalFps += r.sha256_fingerprints.length;
		if (
			typeof r.updated_at === "number" &&
			Number.isFinite(r.updated_at) &&
			r.updated_at > 0 &&
			(latest === null || r.updated_at > latest)
		) {
			latest = r.updated_at;
		}
	}
	return {
		total: rows.length,
		active,
		disabled,
		total_fingerprints: totalFps,
		latest_update_at: latest,
	};
}

export function sortAppsByUpdated(
	rows: readonly AndroidAppView[],
): AndroidAppView[] {
	return [...rows].sort((a, b) => {
		if (b.updated_at !== a.updated_at) return b.updated_at - a.updated_at;
		if (a.package_name < b.package_name) return -1;
		if (a.package_name > b.package_name) return 1;
		return 0;
	});
}

export interface InstallationsSummary {
	total: number;
	unique_sdks: number;
	unique_device_models: number;
	latest_created_at: number | null;
}

export function computeInstallationsSummary(
	rows: readonly AndroidInstallationView[],
): InstallationsSummary {
	const sdks = new Set<string>();
	const models = new Set<string>();
	let latest: number | null = null;
	for (const r of rows) {
		if (r.sdk_version) sdks.add(r.sdk_version);
		if (r.device_model) models.add(r.device_model);
		if (
			typeof r.created_at === "number" &&
			Number.isFinite(r.created_at) &&
			r.created_at > 0 &&
			(latest === null || r.created_at > latest)
		) {
			latest = r.created_at;
		}
	}
	return {
		total: rows.length,
		unique_sdks: sdks.size,
		unique_device_models: models.size,
		latest_created_at: latest,
	};
}

/**
 * Parse a comma/newline-separated textarea of fingerprints into a deduped
 * normalized list. Returns `null` if any entry is unparseable.
 */
export function parseFingerprintsInput(raw: string): string[] | null {
	const entries = raw
		.split(/[\s,]+/)
		.map((v) => v.trim())
		.filter((v) => v.length > 0);
	const seen = new Set<string>();
	const out: string[] = [];
	for (const e of entries) {
		const norm = normalizeFingerprintInput(e);
		if (!norm) return null;
		if (!seen.has(norm)) {
			seen.add(norm);
			out.push(norm);
		}
	}
	return out;
}

export function parseEventListInput(raw: string): string[] | null {
	const entries = raw
		.split(/[\s,]+/)
		.map((v) => v.trim())
		.filter((v) => v.length > 0);
	const seen = new Set<string>();
	const out: string[] = [];
	for (const e of entries) {
		if (!isValidEventName(e)) return null;
		if (!seen.has(e)) {
			seen.add(e);
			out.push(e);
		}
	}
	return out;
}

/**
 * Diff two configs for the dashboard audit preview. Returns a plain object
 * with added/removed/changed arrays; empty arrays = no changes.
 */
export interface ConfigDiffPreview {
	enabled_added: string[];
	enabled_removed: string[];
	disabled_added: string[];
	disabled_removed: string[];
	rate_limit_change: { from: number; to: number } | null;
	flags_changed: Array<{ key: string; from: boolean | null; to: boolean | null }>;
}

export function diffConfig(
	prev: AndroidSdkConfig,
	next: AndroidSdkConfig,
): ConfigDiffPreview {
	const prevEnabled = new Set(prev.enabled_events);
	const nextEnabled = new Set(next.enabled_events);
	const prevDisabled = new Set(prev.disabled_events);
	const nextDisabled = new Set(next.disabled_events);
	const enabled_added = next.enabled_events.filter(
		(v) => !prevEnabled.has(v),
	);
	const enabled_removed = prev.enabled_events.filter(
		(v) => !nextEnabled.has(v),
	);
	const disabled_added = next.disabled_events.filter(
		(v) => !prevDisabled.has(v),
	);
	const disabled_removed = prev.disabled_events.filter(
		(v) => !nextDisabled.has(v),
	);
	const flagKeys = new Set([
		...Object.keys(prev.feature_flags),
		...Object.keys(next.feature_flags),
	]);
	const flags_changed: ConfigDiffPreview["flags_changed"] = [];
	for (const k of flagKeys) {
		const p = prev.feature_flags[k] ?? null;
		const n = next.feature_flags[k] ?? null;
		if (p !== n) flags_changed.push({ key: k, from: p, to: n });
	}
	flags_changed.sort((a, b) => a.key.localeCompare(b.key));
	return {
		enabled_added,
		enabled_removed,
		disabled_added,
		disabled_removed,
		rate_limit_change:
			prev.rate_limit_per_min !== next.rate_limit_per_min
				? { from: prev.rate_limit_per_min, to: next.rate_limit_per_min }
				: null,
		flags_changed,
	};
}

export function diffHasChanges(d: ConfigDiffPreview): boolean {
	return (
		d.enabled_added.length > 0 ||
		d.enabled_removed.length > 0 ||
		d.disabled_added.length > 0 ||
		d.disabled_removed.length > 0 ||
		d.rate_limit_change !== null ||
		d.flags_changed.length > 0
	);
}

export function formatInteger(n: number): string {
	if (!Number.isFinite(n)) return "0";
	return new Intl.NumberFormat("en-US").format(Math.trunc(n));
}

export function formatTimestamp(unixSec: number | null | undefined): string {
	if (
		unixSec === null ||
		unixSec === undefined ||
		!Number.isFinite(unixSec) ||
		unixSec <= 0
	) {
		return "Never";
	}
	const d = new Date(unixSec * 1000);
	return d.toLocaleString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

export function formatActiveLabel(active: boolean): string {
	return active ? "Active" : "Disabled";
}

export function truncate(str: string, max: number): string {
	if (str.length <= max) return str;
	return `${str.slice(0, Math.max(0, max - 1))}…`;
}

export const androidSdkQueryKeys = {
	all: ["android-sdk"] as const,
	apps: () => [...androidSdkQueryKeys.all, "apps"] as const,
	app: (id: string) => [...androidSdkQueryKeys.all, "app", id] as const,
	installations: (appId: string | null) =>
		[...androidSdkQueryKeys.all, "installations", appId ?? "*"] as const,
	config: (appId: string) =>
		[...androidSdkQueryKeys.all, "config", appId] as const,
};
